/**
 * DAILY INSPIRATION LIBRARY — Founder dashboard for the canonical
 * 366-quote Stoic library. Inspect, search, filter, edit, verify,
 * activate/deactivate, preview by date and run integrity checks.
 */
import { useCallback, useEffect, useState } from "react";
import AdminShell from "@/components/admin/AdminShell";
import { adminApi } from "@/lib/adminApi";
import {
  BadgeCheck, AlertTriangle, Search, CalendarDays, ShieldCheck, X, Loader2,
} from "lucide-react";

const STATUS_STYLE = {
  VERIFIED_PRIMARY_SOURCE: "bg-emerald-900/40 text-emerald-300 border-emerald-700/50",
  REVIEW_REQUIRED: "bg-amber-900/40 text-amber-300 border-amber-700/50",
  UNVERIFIED: "bg-zinc-800 text-zinc-400 border-zinc-700",
};

const StatusBadge = ({ status }) => (
  <span
    data-testid="di-status-badge"
    className={`inline-flex items-center gap-1 px-2 py-0.5 border text-[10px] tracking-[0.14em] uppercase ${STATUS_STYLE[status] || STATUS_STYLE.UNVERIFIED}`}
  >
    {status === "VERIFIED_PRIMARY_SOURCE" ? <BadgeCheck size={11} /> : <AlertTriangle size={11} />}
    {status === "VERIFIED_PRIMARY_SOURCE" ? "Verified primary source" : status?.replace("_", " ")}
  </span>
);

const Stat = ({ label, value, testid }) => (
  <div data-testid={testid} className="border border-zinc-800 bg-zinc-950 px-4 py-3">
    <p className="text-[10px] uppercase tracking-[0.2em] text-zinc-500">{label}</p>
    <p className="text-xl text-amber-200 font-serif mt-1">{value ?? "—"}</p>
  </div>
);

const FIELD_GROUPS = [
  ["quote_text", "Quote text", "textarea"],
  ["author", "Author"], ["source_work", "Source work"],
  ["book", "Book"], ["chapter_or_section", "Chapter / section"],
  ["canonical_reference", "Canonical reference"], ["theme", "Theme"],
  ["quote_type", "Quote type (direct_translation | paraphrase)"],
  ["original_language", "Original language"], ["original_text", "Original text", "textarea"],
  ["translator", "Translator"], ["translation_edition", "Translation edition"],
  ["translation_source", "Translation source"],
  ["reflection", "StoicOs commentary (never shown as ancient text)", "textarea"],
];

function QuoteModal({ quote, onClose, onSaved }) {
  const [form, setForm] = useState({ ...quote });
  const [saving, setSaving] = useState(false);
  const [verifying, setVerifying] = useState(false);
  const [verifyNotes, setVerifyNotes] = useState("");
  const [msg, setMsg] = useState("");

  const save = async () => {
    setSaving(true); setMsg("");
    try {
      const patch = {};
      FIELD_GROUPS.forEach(([k]) => { if (form[k] !== quote[k]) patch[k] = form[k]; });
      const res = await adminApi.diPatch(quote.id, patch);
      setMsg(res.status_demoted
        ? "Saved. Material edit detected — status set to REVIEW REQUIRED until re-verified."
        : "Saved.");
      onSaved(res.quote);
    } catch (e) { setMsg(e.message); }
    setSaving(false);
  };

  const toggleActive = async () => {
    const res = await adminApi.diPatch(quote.id, { active: !quote.active });
    onSaved(res.quote);
  };

  const verify = async () => {
    if (verifyNotes.trim().length < 5) { setMsg("Verification notes are required (min 5 chars)."); return; }
    setVerifying(true); setMsg("");
    try {
      const res = await adminApi.diVerify(quote.id, verifyNotes.trim());
      setMsg("Marked as VERIFIED PRIMARY SOURCE.");
      onSaved(res.quote);
    } catch (e) { setMsg(e.message); }
    setVerifying(false);
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/80 flex items-start justify-center overflow-y-auto p-4" data-testid="di-quote-modal">
      <div className="bg-zinc-950 border border-zinc-800 w-full max-w-3xl my-8">
        <div className="flex items-center justify-between border-b border-zinc-800 px-5 py-3">
          <div className="flex items-center gap-3">
            <span className="text-amber-300 font-mono text-sm">{quote.id}</span>
            <span className="text-zinc-500 text-xs">Day {quote.day_number}</span>
            <StatusBadge status={quote.verification_status} />
            <span className={`text-[10px] uppercase tracking-widest ${quote.active ? "text-emerald-400" : "text-zinc-500"}`}>
              {quote.active ? "Active" : "Inactive"}
            </span>
          </div>
          <button data-testid="di-modal-close" onClick={onClose} className="text-zinc-500 hover:text-white"><X size={18} /></button>
        </div>
        <div className="p-5 space-y-4">
          <div className="border border-zinc-800 bg-black/40 p-4">
            <p className="text-[10px] uppercase tracking-[0.2em] text-zinc-500 mb-2">Provenance (verification notes)</p>
            <p data-testid="di-provenance" className="text-xs text-zinc-400 leading-relaxed whitespace-pre-wrap">{quote.verification_notes || "—"}</p>
          </div>
          <div className="grid sm:grid-cols-2 gap-3">
            {FIELD_GROUPS.map(([key, label, kind]) => (
              <label key={key} className={kind === "textarea" ? "sm:col-span-2" : ""}>
                <span className="block text-[10px] uppercase tracking-[0.18em] text-zinc-500 mb-1">{label}</span>
                {kind === "textarea" ? (
                  <textarea data-testid={`di-edit-${key}`} rows={key === "quote_text" ? 4 : 2}
                    value={form[key] || ""} onChange={(e) => setForm({ ...form, [key]: e.target.value })}
                    className="w-full bg-zinc-900 border border-zinc-800 px-3 py-2 text-sm text-zinc-200 focus:border-amber-600 outline-none" />
                ) : (
                  <input data-testid={`di-edit-${key}`} value={form[key] || ""}
                    onChange={(e) => setForm({ ...form, [key]: e.target.value })}
                    className="w-full bg-zinc-900 border border-zinc-800 px-3 py-2 text-sm text-zinc-200 focus:border-amber-600 outline-none" />
                )}
              </label>
            ))}
          </div>
          <p className="text-[11px] text-amber-500/80">
            Editing the quote text, attribution or classical reference automatically revokes the
            verified status until the quotation is re-verified against the primary source.
          </p>
          {msg && <p data-testid="di-modal-msg" className="text-xs text-amber-300">{msg}</p>}
          <div className="flex flex-wrap items-center gap-3 pt-1">
            <button data-testid="di-save-btn" onClick={save} disabled={saving}
              className="bg-amber-500 text-black px-5 py-2 text-xs uppercase tracking-[0.2em] font-semibold hover:bg-amber-400 disabled:opacity-50">
              {saving ? "Saving…" : "Save changes"}
            </button>
            <button data-testid="di-toggle-active-btn" onClick={toggleActive}
              className="border border-zinc-700 px-5 py-2 text-xs uppercase tracking-[0.2em] text-zinc-300 hover:border-amber-600">
              {quote.active ? "Deactivate" : "Activate"}
            </button>
          </div>
          {quote.verification_status !== "VERIFIED_PRIMARY_SOURCE" && (
            <div className="border border-emerald-900/60 bg-emerald-950/20 p-4 space-y-2">
              <p className="text-[10px] uppercase tracking-[0.2em] text-emerald-400">Re-verify against primary source</p>
              <textarea data-testid="di-verify-notes" rows={2} value={verifyNotes}
                onChange={(e) => setVerifyNotes(e.target.value)}
                placeholder="How was this quotation checked (edition, page/section)?"
                className="w-full bg-zinc-900 border border-zinc-800 px-3 py-2 text-sm text-zinc-200 outline-none" />
              <button data-testid="di-verify-btn" onClick={verify} disabled={verifying}
                className="bg-emerald-600 text-white px-4 py-2 text-xs uppercase tracking-[0.2em] hover:bg-emerald-500 disabled:opacity-50">
                <ShieldCheck size={12} className="inline mr-1 -mt-0.5" /> Mark verified
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

export default function AdminDailyInspiration() {
  const [meta, setMeta] = useState(null);
  const [quotes, setQuotes] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [pages, setPages] = useState(1);
  const [loading, setLoading] = useState(true);
  const [filters, setFilters] = useState({ search: "", author: "", work: "", theme: "", status: "", active: "" });
  const [selected, setSelected] = useState(null);
  const [previewDate, setPreviewDate] = useState("");
  const [preview, setPreview] = useState(null);
  const [integrity, setIntegrity] = useState(null);
  const [checking, setChecking] = useState(false);

  const load = useCallback(async (p = 1, f = filters) => {
    setLoading(true);
    try {
      const data = await adminApi.diList({ ...f, page: p, limit: 50 });
      setQuotes(data.quotes); setTotal(data.total); setPage(data.page); setPages(data.pages);
    } finally { setLoading(false); }
  }, [filters]);

  useEffect(() => { adminApi.diMeta().then(setMeta).catch(() => {}); }, []);
  useEffect(() => { load(1); /* eslint-disable-next-line */ }, [filters.author, filters.work, filters.theme, filters.status, filters.active]);

  const runPreview = async () => {
    if (!previewDate) return;
    setPreview(await adminApi.diPreview(previewDate));
  };

  const runIntegrity = async () => {
    setChecking(true);
    try { setIntegrity(await adminApi.diIntegrity()); } finally { setChecking(false); }
  };

  const onSaved = (q) => {
    setSelected(q);
    setQuotes((list) => list.map((x) => (x.id === q.id ? q : x)));
    adminApi.diMeta().then(setMeta).catch(() => {});
  };

  const sel = (k) => (e) => setFilters({ ...filters, [k]: e.target.value });

  return (
    <AdminShell>
      <div data-testid="daily-inspiration-library" className="space-y-6">
        <div>
          <h1 className="font-serif text-2xl text-amber-100 tracking-wide">Daily Inspiration Library</h1>
          <p className="text-zinc-500 text-sm mt-1">
            The canonical 366-quotation Stoic library — one database, one daily quote, all platforms.
          </p>
        </div>

        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          <Stat label="Quotations" value={meta?.stats?.total} testid="di-stat-total" />
          <Stat label="Verified primary source" value={meta?.stats?.verified} testid="di-stat-verified" />
          <Stat label="Active" value={meta?.stats?.active} testid="di-stat-active" />
          <Stat label="Review required" value={meta?.stats?.review_required} testid="di-stat-review" />
        </div>

        {/* Preview by date + integrity */}
        <div className="grid lg:grid-cols-2 gap-4">
          <div className="border border-zinc-800 bg-zinc-950 p-4 space-y-3">
            <p className="text-[10px] uppercase tracking-[0.2em] text-zinc-500 flex items-center gap-2"><CalendarDays size={12} /> Preview by calendar date</p>
            <div className="flex gap-2">
              <input data-testid="di-preview-date" type="date" value={previewDate}
                onChange={(e) => setPreviewDate(e.target.value)}
                className="bg-zinc-900 border border-zinc-800 px-3 py-2 text-sm text-zinc-200 outline-none" />
              <button data-testid="di-preview-btn" onClick={runPreview}
                className="border border-amber-700 text-amber-300 px-4 py-2 text-xs uppercase tracking-[0.2em] hover:bg-amber-900/20">Preview</button>
            </div>
            {preview && (
              <div data-testid="di-preview-result" className="border-l-2 border-amber-600 pl-3 text-sm text-zinc-300">
                <p className="italic font-serif">“{preview.quote_text}”</p>
                <p className="text-xs text-zinc-500 mt-1">
                  — {preview.author}, {preview.canonical_reference} · Day {preview.mapped_day_number} · {preview.id}
                </p>
              </div>
            )}
          </div>
          <div className="border border-zinc-800 bg-zinc-950 p-4 space-y-3">
            <p className="text-[10px] uppercase tracking-[0.2em] text-zinc-500 flex items-center gap-2"><ShieldCheck size={12} /> Integrity &amp; duplicate checks</p>
            <button data-testid="di-integrity-btn" onClick={runIntegrity} disabled={checking}
              className="border border-amber-700 text-amber-300 px-4 py-2 text-xs uppercase tracking-[0.2em] hover:bg-amber-900/20 disabled:opacity-50">
              {checking ? "Checking…" : "Run integrity check"}
            </button>
            {integrity && (
              <div data-testid="di-integrity-result" className="text-xs space-y-1">
                <p className={integrity.ok ? "text-emerald-400" : "text-amber-400"}>
                  {integrity.ok ? "✓ ALL CHECKS PASSED" : "Issues detected"} — {integrity.total_quotes} quotations
                </p>
                <p className="text-zinc-500">
                  Duplicate ids: {integrity.duplicate_ids.length} · Duplicate days: {integrity.duplicate_days.length} ·
                  Duplicate texts: {integrity.duplicate_texts.length} · Missing days: {integrity.missing_days.length} ·
                  Missing sources: {integrity.missing_sources.length} · Not verified: {integrity.not_verified.length} ·
                  Inactive: {integrity.inactive.length} · Feb 29 (day 366): {integrity.day_366_ok ? "covered" : "MISSING"}
                </p>
                {integrity.rotation_gaps.length > 0 && (
                  <p className="text-amber-400">Rotation gaps (days served by substitute): {integrity.rotation_gaps.join(", ")}</p>
                )}
              </div>
            )}
          </div>
        </div>

        {/* Filters */}
        <div className="flex flex-wrap gap-2 items-center">
          <div className="relative">
            <Search size={13} className="absolute left-3 top-1/2 -translate-y-1/2 text-zinc-600" />
            <input data-testid="di-search-input" value={filters.search} placeholder="Search quote, reference, id…"
              onChange={sel("search")} onKeyDown={(e) => e.key === "Enter" && load(1)}
              className="bg-zinc-900 border border-zinc-800 pl-8 pr-3 py-2 text-sm text-zinc-200 outline-none w-64" />
          </div>
          <select data-testid="di-filter-author" value={filters.author} onChange={sel("author")} className="bg-zinc-900 border border-zinc-800 px-2 py-2 text-sm text-zinc-300">
            <option value="">All authors</option>
            {meta?.authors?.map((a) => <option key={a} value={a}>{a}</option>)}
          </select>
          <select data-testid="di-filter-work" value={filters.work} onChange={sel("work")} className="bg-zinc-900 border border-zinc-800 px-2 py-2 text-sm text-zinc-300">
            <option value="">All works</option>
            {meta?.works?.map((w) => <option key={w} value={w}>{w}</option>)}
          </select>
          <select data-testid="di-filter-theme" value={filters.theme} onChange={sel("theme")} className="bg-zinc-900 border border-zinc-800 px-2 py-2 text-sm text-zinc-300">
            <option value="">All themes</option>
            {meta?.themes?.map((t) => <option key={t} value={t}>{t}</option>)}
          </select>
          <select data-testid="di-filter-status" value={filters.status} onChange={sel("status")} className="bg-zinc-900 border border-zinc-800 px-2 py-2 text-sm text-zinc-300">
            <option value="">All statuses</option>
            <option value="VERIFIED_PRIMARY_SOURCE">Verified primary source</option>
            <option value="REVIEW_REQUIRED">Review required</option>
            <option value="UNVERIFIED">Unverified</option>
          </select>
          <select data-testid="di-filter-active" value={filters.active} onChange={sel("active")} className="bg-zinc-900 border border-zinc-800 px-2 py-2 text-sm text-zinc-300">
            <option value="">Active + inactive</option>
            <option value="true">Active only</option>
            <option value="false">Inactive only</option>
          </select>
          <button data-testid="di-search-btn" onClick={() => load(1)} className="border border-zinc-700 px-4 py-2 text-xs uppercase tracking-[0.18em] text-zinc-300 hover:border-amber-600">Search</button>
          <span className="text-xs text-zinc-500 ml-auto">{total} results</span>
        </div>

        {/* Table */}
        <div className="border border-zinc-800 overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left text-[10px] uppercase tracking-[0.18em] text-zinc-500 border-b border-zinc-800 bg-zinc-950">
                <th className="px-3 py-2">Day</th><th className="px-3 py-2">Quote</th>
                <th className="px-3 py-2">Author</th><th className="px-3 py-2">Reference</th>
                <th className="px-3 py-2">Theme</th><th className="px-3 py-2">Status</th>
                <th className="px-3 py-2">Active</th>
              </tr>
            </thead>
            <tbody data-testid="di-quotes-table">
              {loading ? (
                <tr><td colSpan={7} className="px-3 py-8 text-center text-zinc-500"><Loader2 className="animate-spin inline" size={16} /></td></tr>
              ) : quotes.map((q) => (
                <tr key={q.id} data-testid={`di-row-${q.id}`} onClick={() => setSelected(q)}
                  className="border-b border-zinc-900 hover:bg-zinc-900/60 cursor-pointer align-top">
                  <td className="px-3 py-2 text-amber-300 font-mono text-xs whitespace-nowrap">{q.day_number}</td>
                  <td className="px-3 py-2 text-zinc-300 font-serif italic max-w-md">
                    {q.quote_text.length > 130 ? q.quote_text.slice(0, 130) + "…" : q.quote_text}
                  </td>
                  <td className="px-3 py-2 text-zinc-400 whitespace-nowrap">{q.author}</td>
                  <td className="px-3 py-2 text-zinc-500 text-xs whitespace-nowrap">{q.canonical_reference}</td>
                  <td className="px-3 py-2 text-zinc-500 text-xs">{q.theme}</td>
                  <td className="px-3 py-2"><StatusBadge status={q.verification_status} /></td>
                  <td className="px-3 py-2 text-xs">{q.active
                    ? <span className="text-emerald-400">●</span>
                    : <span className="text-zinc-600">○</span>}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <div className="flex items-center gap-3 text-xs text-zinc-500">
          <button data-testid="di-prev-page" disabled={page <= 1} onClick={() => load(page - 1)} className="border border-zinc-800 px-3 py-1 disabled:opacity-40">‹ Prev</button>
          <span>Page {page} / {pages}</span>
          <button data-testid="di-next-page" disabled={page >= pages} onClick={() => load(page + 1)} className="border border-zinc-800 px-3 py-1 disabled:opacity-40">Next ›</button>
        </div>
      </div>
      {selected && <QuoteModal quote={selected} onClose={() => setSelected(null)} onSaved={onSaved} />}
    </AdminShell>
  );
}
